import { useState, useEffect, useCallback } from 'react';
import { api, apiSchemas } from '@verifime/api-definition';
import { OrganisationUser } from '@verifime/components';
import { TNotificationHandler } from '../../types';
import { z } from 'zod';

type OrganisationNotificationPreference = z.infer<
  typeof apiSchemas.OrganisationNotificationPreferenceResponse
>;

export type TOrganisationUserWithId = OrganisationUser & {
  id: string;
};

/**
 * Custom hook to manage organisation notification preferences.
 */
export function useOrganisationNotificationData({
  organisationId,
  successNotificationHandler = console.log,
  errorNotificationHandler = console.error,
}: Readonly<{
  organisationId: string;
  successNotificationHandler?: TNotificationHandler;
  errorNotificationHandler?: TNotificationHandler;
}>) {
  const [notifications, setNotifications] = useState<TOrganisationUserWithId[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const convertEmailNotificationToOrgUser = (notification: OrganisationNotificationPreference) => ({
    ...notification,
    id: notification.id,
    email: notification.detail,
  });

  const fetchData = useCallback(async () => {
    if (!organisationId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await api
        .getV1organisationOrganisationIdnotificationpreferences({
          params: { organisationId },
          queries: {
            status: 'ACTIVE',
            type: 'EMAIL',
          },
        })
        .then((response) => response.map(convertEmailNotificationToOrgUser));
      setNotifications(data as TOrganisationUserWithId[]);
    } catch (e) {
      setError(e as Error);
      errorNotificationHandler('Failed to load notification preferences.');
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, errorNotificationHandler]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addRecord = async (newRecord: Pick<TOrganisationUserWithId, 'email'>) => {
    try {
      const notification = await api
        .postV1organisationOrganisationIdnotificationpreferencestypeType(
          { detail: newRecord.email },
          {
            params: {
              organisationId,
              type: 'EMAIL',
            },
          },
        )
        .then(convertEmailNotificationToOrgUser);
      setNotifications((prev) => [...prev, notification as TOrganisationUserWithId]);
      successNotificationHandler('Added');
    } catch (err) {
      errorNotificationHandler('Failed to add notification.');
      throw err;
    } finally {
      await fetchData();
    }
  };

  const removeRecord = async (userToRemove: TOrganisationUserWithId) => {
    try {
      await api.patchV1organisationnotificationpreferencesPreferenceIddeactivate(undefined, {
        params: { preferenceId: userToRemove.id },
      });
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== userToRemove.id),
      );
      successNotificationHandler('Removed');
    } catch (err) {
      errorNotificationHandler('Failed to remove notification.');
      throw err;
    }
  };

  return {
    notifications,
    isLoading,
    error,
    addRecord,
    removeRecord,
  };
}
